import {useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {Box, Button, Grid} from '@mui/material';

import {AutoCompleteFilter} from '../../../../components/AutoCompleteFilter/AutoCompleteFilter';
import {EmpionTooltip} from '../../../../components/EmpionTooltip/EmpionTooltip';
import {greenColors} from '../../../../layouts/theme/designSystem';
import {Option} from '../../../../types/shared-types';

export interface ScreeningApplicantFilters {
  status: Option[];
  location: Option[];
  position: Option[];
  department: Option[];
}

interface Props {
  locationOptions: Option[];
  statusOptions: Option[];
  positionOptions: Option[];
  departmentOptions: Option[];
  initialStatus?: Option[];
  initialLocation?: Option[];
  initialPosition?: Option[];
  initialDepartment?: Option[];
  filterBy: (filter: ScreeningApplicantFilters) => void;
}

export const ApplicantScreeningFilters = ({
  locationOptions,
  positionOptions,
  statusOptions,
  departmentOptions,
  initialStatus = [],
  initialLocation = [],
  initialPosition = [],
  initialDepartment = [],
  filterBy,
}: Props) => {
  const {t: filtersText} = useTranslation('translation', {keyPrefix: 'screeningModule.additionalFilters'});
  const [filters, setFilters] = useState<ScreeningApplicantFilters>({
    status: [],
    location: [],
    position: [],
    department: [],
  });
  const [resetKey, setResetKey] = useState(0);
  const hadFiltersRef = useRef(false);

  useEffect(() => {
    const nextFilters: ScreeningApplicantFilters = {
      status: initialStatus,
      location: initialLocation,
      position: initialPosition,
      department: initialDepartment,
    };
    setFilters(nextFilters);
    const hasListFilters = Object.values(nextFilters).some((entry) => entry.length > 0);
    if (hadFiltersRef.current && !hasListFilters) {
      setResetKey((value) => value + 1);
    }
    hadFiltersRef.current = hasListFilters;
  }, [initialStatus, initialLocation, initialPosition, initialDepartment]);

  const onSelect = (filter: keyof ScreeningApplicantFilters) => (options: Option[]) => {
    const update = {
      ...filters,
      [filter]: options,
    };

    setFilters(update);
    filterBy(update);
  };

  const onReset = () => {
    const resetFilters: ScreeningApplicantFilters = {
      status: [],
      location: [],
      position: [],
      department: [],
    };
    setFilters(resetFilters);
    setResetKey((value) => value + 1);
    filterBy(resetFilters);
  };

  return (
    <Grid>
      <Grid container gap='16px' flexWrap='wrap' alignItems='flex-end'>
        <AutoCompleteFilter
          key={`position-${resetKey}`}
          sx={{width: {xs: '100%', sm: '223px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
          label={filtersText('allJobPostings') as string}
          options={positionOptions}
          currentOptions={filters.position}
          onSelect={onSelect('position')}
        />
        <AutoCompleteFilter
          key={`location-${resetKey}`}
          sx={{width: {xs: '100%', sm: '223px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
          label={filtersText('allLocations') as string}
          options={locationOptions}
          currentOptions={filters.location}
          onSelect={onSelect('location')}
        />
        <AutoCompleteFilter
          key={`department-${resetKey}`}
          sx={{width: {xs: '100%', sm: '223px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
          label={filtersText('allDepartments') as string}
          options={departmentOptions}
          currentOptions={filters.department}
          onSelect={onSelect('department')}
        />
        <AutoCompleteFilter
          key={`status-${resetKey}`}
          sx={{width: {xs: '100%', sm: '223px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
          label={filtersText('allStatuses') as string}
          options={statusOptions}
          currentOptions={filters.status}
          onSelect={onSelect('status')}
        />
        <Box sx={{width: {xs: '100%', sm: 'auto'}, display: 'flex', alignItems: 'flex-end'}}>
          <EmpionTooltip title={filtersText('reset') as string} isEnabled placement='top'>
            <Button
              type='button'
              variant='outlined'
              onClick={onReset}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                borderRadius: '5px',
                textTransform: 'none',
                borderColor: greenColors[700],
                color: 'text.primary',
              }}
            >
              <RestartAltIcon fontSize='small' />
            </Button>
          </EmpionTooltip>
        </Box>
      </Grid>
    </Grid>
  );
};
